/**
 * XPEN-2808 / XPEN-2811: shared AdditionalFieldsModal filter lists and config-driven
 * field visibility for expense.js and invoice.js. Filter lists declare which fields a
 * row trigger may pass to the modal; per-tenant gates mirror AdditionalFieldsModal
 * (export_format + gl_account_segment_configuration).
 */

import Utils from './utils';
import { officeIsCompany } from './gl-segment-rules';

export const ACTIVITY_ONLY_ADDITIONAL_FIELDS = ['Activity'];

const BILLABLE_ADDITIONAL_FIELD_KEYS = [
    'WorkQuantity',
    'WorkRate',
    'GLDepartement',
    'Department',
    'Activity',
    'Attorney',
    'IncurringEmployee',
    'AuthorizingEmployee',
    'Jurisdiction',
    'HoldDate',
    'Location',
    'Project',
    'Reference',
    'ToBillAmount',
    'CalculateAmountsAutomatically',
    'PreBillDisbursementNarrative',
    'Comments',
];

const GL_ADDITIONAL_FIELD_KEYS = [
    'Office',
    'GLDepartement',
    'Department',
    'GLSection',
    'GLPracticeGroup',
    'GLTimekeeper',
    'Employee',
    'ProfitCenter',
    'Jurisdiction',
    'Project',
    'Comments',
];

const ADERANT_MODAL_FIELDS = new Set([
    'Department',
    'Employee',
    'IncurringEmployee',
    'AuthorizingEmployee',
    'ProfitCenter',
    'Jurisdiction',
    'HoldDate',
    'Location',
    'Project',
    'Reference',
    'ToBillAmount',
    'CalculateAmountsAutomatically',
    'PreBillDisbursementNarrative',
]);

/**
 * @param {object|null|undefined} data
 * @returns {Record<string, { active?: boolean }>}
 */
export function getGlSegmentConfig(data) {
    return data?.config?.gl_account_segment_configuration ?? {};
}

/**
 * @param {object|null|undefined} data
 * @param {string} segmentKey
 * @returns {boolean}
 */
export function isGlSegmentActive(data, segmentKey) {
    return getGlSegmentConfig(data)[segmentKey]?.active === true;
}

/**
 * @param {object|null|undefined} data
 * @returns {boolean}
 */
export function shouldHideLineGlUnitColumn(data) {
    return Utils.isExportFormat(data, 'aderant')
        || officeIsCompany(data?.config)
        || data?.config?.frontend_invoice?.hide_unit_in_gl_details_table === true;
}

/**
 * @param {object|null|undefined} data
 * @returns {boolean}
 */
export function shouldShowLineGlUnitColumn(data) {
    return isGlSegmentActive(data, 'GLUnit') && !shouldHideLineGlUnitColumn(data);
}

/**
 * @param {object|null|undefined} data
 * @returns {boolean}
 */
export function shouldShowHeaderGlUnitField(data) {
    return isGlSegmentActive(data, 'GLUnit');
}

/**
 * XPEN-3542: SurePoint's cost date is the voucher's transaction date, taken from the
 * invoice header, so a per-line work date has nothing to feed.
 *
 * @param {object|null|undefined} data
 * @returns {boolean}
 */
export function shouldHideLineWorkDateColumn(data) {
    return Utils.isExportFormat(data, 'surepoint');
}

/**
 * XPEN-3543: SurePoint fills a cost attorney on every client cost line, so the line
 * carries it inline and the invoice header carries the default.
 *
 * @param {object|null|undefined} data
 * @returns {boolean}
 */
export function shouldShowAttorneyFields(data) {
    return Utils.isExportFormat(data, 'surepoint');
}

/**
 * XPEN-3543: the attorney a line posts with — its own when it has one, the invoice
 * header's otherwise.
 *
 * @param {object|null|undefined} item
 * @param {object|null|undefined} invoiceJson
 * @returns {object|string|undefined}
 */
export function effectiveLineAttorney(item, invoiceJson) {
    const own = item?.AttorneyId;
    const ownCode = typeof own === 'object' ? own?.value : own;
    if (ownCode != null && String(ownCode).trim() !== '') {
        return own;
    }
    return invoiceJson?.AttorneyId;
}

/**
 * XPEN-3542: the header Transaction Date is optional per tenant, but on SurePoint it is
 * the only date feeding the exported cost, so it is always shown there.
 *
 * @param {object|null|undefined} data
 * @returns {boolean}
 */
export function shouldShowHeaderTransactionDateField(data) {
    if (Utils.isExportFormat(data, 'aderant')) {
        return false;
    }
    return Boolean(data?.config?.frontend_invoice?.TranDate) || Utils.isExportFormat(data, 'surepoint');
}

/**
 * @param {object|null|undefined} data
 * @returns {boolean}
 */
export function shouldShowActivityInlineTrigger(data) {
    return !Utils.isExportFormat(data, 'aderant') && !Utils.isExportFormat(data, 'surepoint');
}

/**
 * @param {object|null|undefined} data
 * @returns {boolean}
 */
export function shouldUseInvoiceOfficeLabel(data) {
    return Utils.isExportFormat(data, 'elite') && isGlSegmentActive(data, 'GLOffice');
}

/**
 * Mirrors AdditionalFieldsModal field gates (export format + segment config).
 *
 * @param {object|null|undefined} data
 * @param {string} fieldKey
 * @param {{ tableKind?: 'billable'|'gl', model?: string, isLegalEngine?: boolean }} [context]
 * @returns {boolean}
 */
export function isEditorAdditionalFieldAllowed(data, fieldKey, context = {}) {
    const { tableKind = 'billable', model, isLegalEngine = true } = context;
    const isAderant = Utils.isExportFormat(data, 'aderant');
    const isElite = Utils.isExportFormat(data, 'elite');
    const isSurePoint = Utils.isExportFormat(data, 'surepoint');

    if (fieldKey === 'WorkTotal') {
        return model === 'CMPA';
    }
    // XPEN-3543: the SurePoint cost attorney moved out of this window onto the line
    // itself, with the invoice header as its default, so the window never offers it.
    if (fieldKey === 'Attorney') {
        return false;
    }
    if (fieldKey === 'WorkQuantity') {
        return !isSurePoint;
    }
    if (fieldKey === 'WorkRate' || fieldKey === 'Activity') {
        return !isAderant && !isSurePoint;
    }
    if (fieldKey === 'Comments') {
        return !isAderant;
    }
    if (ADERANT_MODAL_FIELDS.has(fieldKey)) {
        return isAderant;
    }
    if (fieldKey === 'GLDepartement') {
        return !isAderant && isGlSegmentActive(data, 'GLDepartement');
    }
    if (fieldKey === 'GLSection') {
        return !isAderant && tableKind === 'gl' && isGlSegmentActive(data, 'GLSection');
    }
    if (fieldKey === 'GLPracticeGroup') {
        return isElite && tableKind === 'gl' && isGlSegmentActive(data, 'GLPracticeGroup');
    }
    if (fieldKey === 'GLTimekeeper') {
        return isElite && tableKind === 'gl' && isGlSegmentActive(data, 'GLTimekeeper');
    }
    if (fieldKey === 'Office') {
        return isLegalEngine;
    }
    return true;
}

/**
 * @param {{ model?: string }} [options]
 * @returns {string[]}
 */
function buildUnfilteredBillableAdditionalFieldsFilter(options = {}) {
    const model = options.model;
    const cmpaExtras = model === 'CMPA' ? ['WorkTotal'] : [];
    const withWorkRate = ['WorkQuantity', 'WorkRate', ...cmpaExtras];
    const tail = BILLABLE_ADDITIONAL_FIELD_KEYS.filter(
        (key) => key !== 'WorkQuantity' && key !== 'WorkRate'
    );
    return [...withWorkRate, ...tail];
}

/**
 * @param {object|null|undefined} data
 * @param {{ model?: string, isLegalEngine?: boolean }} [options]
 * @returns {string[]}
 */
export function buildBillableAdditionalFieldsFilter(data, options = {}) {
    const keys = buildUnfilteredBillableAdditionalFieldsFilter(options);
    return keys.filter((key) => isEditorAdditionalFieldAllowed(data, key, {
        ...options,
        tableKind: 'billable',
    }));
}

/**
 * @param {object|null|undefined} data
 * @param {{ isLegalEngine?: boolean }} [options]
 * @returns {string[]}
 */
export function buildGlAdditionalFieldsFilter(data, options = {}) {
    return GL_ADDITIONAL_FIELD_KEYS.filter((key) => isEditorAdditionalFieldAllowed(data, key, {
        ...options,
        tableKind: 'gl',
    }));
}

/**
 * @param {object|null|undefined} data
 * @returns {string[]}
 */
export function buildActivityAdditionalFieldsFilter(data) {
    return shouldShowActivityInlineTrigger(data) ? [...ACTIVITY_ONLY_ADDITIONAL_FIELDS] : [];
}

/**
 * @param {object|null|undefined} data
 * @param {{ isBillable?: boolean }} [options]
 * @returns {string[]}
 */
export function buildAderantRequiredFieldsFilter(data, options = {}) {
    const { isBillable = false } = options;
    if (!Utils.isExportFormat(data, 'aderant')) {
        return [];
    }
    return isBillable
        ? ['IncurringEmployee', 'AuthorizingEmployee']
        : ['Employee', 'Department'];
}
